import { createFileRoute } from "@tanstack/react-router";
import Link from "@/components/link";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { SetupPanel } from "@/components/setup-panel";
import { SearchInput } from "@/components/search-input";
import { DataTable } from "@/components/data-table";
import { ChartCard } from "@/components/charts/chart-card";
import { CompareNote, DataNote } from "@/components/analytics/report-shell";
import { ParamChips } from "@/components/analytics/param-chips";
import { StoreSampleBanner } from "@/components/analytics/store-sample-banner";
import { SkProductsPage } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { DEFAULT_PRESET } from "@shared/analytics-range";
import { fmtNum } from "@shared/format";
import type { ProductsPage, ShowFilter } from "@shared/pages/products";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/products/")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<ProductsPage>("products", deps));
  },
  component: ProductsRoute,
});

const SHOW: Record<ShowFilter, { title: string; info?: string }> = {
  all: { title: "Products" },
  advertised: { title: "Products with ad spend", info: "Products Google Ads spent on in this range, and what came back." },
  "not-advertised": { title: "Products with no ad spend", info: "Products that sold in this range without any Google Ads spend." },
};

function ProductsRoute() {
  useTitle("Products");
  const search = Route.useSearch();
  return (
    <PageQuery<ProductsPage> name="products" search={search} skeleton={<SkProductsPage />}>
      {(page) => <Products page={page} />}
    </PageQuery>
  );
}

function Products({ page }: { page: ProductsPage }) {
  const { range: r, status, show } = page;
  const count = page.rows.length;

  return (
    <div className="page-shell">
      <PageHeader title={SHOW[show].title} info={SHOW[show].info} actions={<DateRangeFilter presets={r.presets} defaultKey={DEFAULT_PRESET} compare />} />
      <StoreSampleBanner sales={page.sources.sales === "sample"} ads={page.sources.ads === "sample"} ga={page.sources.ga === "sample"} salesWhat="Product" />
      <DataNote status={status} staleAfterDays={3} />
      {status && !status.onFile.products && (
        <SetupPanel title="No Shopify products on file yet" className="mb-4">
          Shopify is connected. Products show here after the first sync finishes.
        </SetupPanel>
      )}

      <CompareNote range={r} />

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="w-full @2xl:w-80">
          <SearchInput placeholder="Find a product…" />
        </div>
        {page.hasAds && (
          <ParamChips
            param="show"
            defaultValue="all"
            options={[
              { value: "all", label: "All" },
              { value: "advertised", label: "With ad spend" },
              { value: "not-advertised", label: "No ad spend" },
            ]}
          />
        )}
        <ParamChips
          param="sold"
          defaultValue="any"
          options={[
            { value: "any", label: "Sold or not" },
            { value: "yes", label: "Sold" },
            { value: "no", label: "Not sold" },
          ]}
        />
      </div>

      <ChartCard className="mt-4" title={`${fmtNum(count)} ${count === 1 ? "product" : "products"}`} hint={[page.sourceLine, page.columnsNote].filter(Boolean).join(" ")}>
        <DataTable columns={page.columns} rows={page.rows} pageSize={25} defaultSort="netRevenue" rowKey="productId" emptyText={page.emptyText} />
        {page.missing.length > 0 && (
          <div className="mt-4 space-y-1 border-t border-border pt-3 text-xs text-muted">
            {page.missing.map((line) => (
              <p key={line}>
                {line}{" "}
                {page.isAdmin ? (
                  <Link href="/settings" className="font-semibold text-accent-dark hover:underline">
                    Match products in Settings
                  </Link>
                ) : (
                  "An admin can match it in Settings."
                )}
              </p>
            ))}
          </div>
        )}
      </ChartCard>
    </div>
  );
}
